import { Button, Icon, PageTitle, Select, SelectOption, ToastLevel, useIntl, useToast } from "@metsooutotec/modes-react-components"
import { observer } from "mobx-react-lite"
import { FC, ReactNode, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { getAbleToCommitSensorsNumber } from "../stock-managment/stock-management-helper"
import StockAllocationDeleteModal from "./stock-allocation-delete-modal"
import StockAllocateModal from "./stock-allocation-modal/stock-allocation-modal"
import { StockAllocationRow } from "features/iot/stock-management/models/stock-management-models"

import "./stock-allocation-styles.css"
import { groupSensorsForStockAllocation } from "./stock-allocation-helper"
import { getRelatedAllocatedSensorsMacAddress } from "./stock-allocation-modal/stock-allocation-modal-helper"
import sensorBatchStore from "features/iot/sensor-batch/stores/sensor-batch-store"
import ListView, { TemplateItem } from "components/utils/list-view/list-view"
import { itemViewDetailsContainerStyles } from "components/common-style/common-style"
import { convertValueToOption } from "components/common/select-component-helper"

const StockAllocationListView: FC = observer(() => {
    const { formatMessage } = useIntl()
    const { setMessage } = useToast()
    const navigate = useNavigate()
    const params = useParams()
    const { allPartNumbers } = sensorBatchStore
    const [openAddAllocation, setOpenAddAllocation] = useState<boolean>(false)
    const [openDeleteAllocation, setOpenDeleteAllocation] = useState<boolean>(false)
    const [stockAllocationToEdit, setStockAllocationToEdit] = useState<StockAllocationRow | undefined>(undefined)
    const [stockAllocationToDelete, setStockAllocationToDelete] = useState<string[]>([])
    const [searchInput, setSearchInput] = useState<SelectOption>({
        text: "",
        value: "",
    })

    const listOfPartNumbers = useMemo(() => {
        return allPartNumbers.length > 0
            ? allPartNumbers.map(h => ({
                  text: h,
                  value: h,
              }))
            : []
    }, [allPartNumbers])
    const tableContainerStyles = {
        borderTop: "2px solid gray",
    }

    // Sync state with URL params on initial load or when params change
    useEffect(() => {
        if (params.partNumber && params.partNumber !== searchInput.text) {
            setSearchInput({ text: params.partNumber, value: params.partNumber })
        }
    }, [params.partNumber])

    // Navigate when search input changes due to user action
    useEffect(() => {
        if (searchInput.text && searchInput.text !== params.partNumber) {
            navigate(`/stock-management/stock-allocation/${searchInput.text}`)
        }
    }, [searchInput])

    const dataTable = useMemo(() => {
        return groupSensorsForStockAllocation(searchInput.text)
    }, [searchInput, sensorBatchStore.sensorBatches])

    const sensorsAbleToCommitNumber = useMemo(() => {
        return getAbleToCommitSensorsNumber(searchInput.text)
    }, [searchInput, sensorBatchStore.sensorBatches])

    const onSearchChange = (resultValue: SelectOption) => {
        setSearchInput(resultValue)
    }
    const rightButtonsFunctions = {
        add: () => {
            if (!searchInput.text) {
                setMessage("Please choose part number", ToastLevel.WARNING, 3000)
            } else if (sensorsAbleToCommitNumber < 1) {
                setMessage("No Sensors Available", ToastLevel.WARNING, 3000)
            } else {
                setOpenAddAllocation(true)
            }
        },
        edit: (event: React.MouseEvent, stockAllocation: StockAllocationRow) => {
            event.stopPropagation()
            if (!searchInput.text) {
                setMessage("Please choose part number", ToastLevel.WARNING, 3000)
            } else {
                setStockAllocationToEdit(stockAllocation)
                setOpenAddAllocation(true)
            }
        },
        closeModal: () => {
            setStockAllocationToEdit(undefined)
            setOpenAddAllocation(false)
        },
        delete: (event: React.MouseEvent, stockAllocation: StockAllocationRow) => {
            event.stopPropagation()
            setOpenDeleteAllocation(true)
            const sensorsMacAddress = getRelatedAllocatedSensorsMacAddress(searchInput.text, stockAllocation.customerId, stockAllocation.siteId)
            setStockAllocationToDelete(sensorsMacAddress)
        },
        dispatch: (event: React.MouseEvent, stockAllocation: StockAllocationRow) => {
            event.stopPropagation()
            const { customerName, siteName } = stockAllocation
            if (customerName && siteName && searchInput) {
                navigate(`/stock-management/dispatch/${searchInput.text}/${customerName}/${siteName}`)
            }
        },
    }
    const onClickRow = (stockAllocation: StockAllocationRow) => {
        navigate(`/stock-management/allocated/${params.partNumber}/${stockAllocation.customerName}/${stockAllocation.siteName}`)
    }

    const rightButtons: ReactNode = (
        <div style={{ display: "flex", gap: "0.5em" }}>
            <Button icon='icon-submitted-16' labelPosition='right' onClick={rightButtonsFunctions.add} primary>
                {formatMessage("stockAllocation.addAllocation")}
            </Button>
        </div>
    )

    const tableTemplate: TemplateItem<StockAllocationRow>[] = [
        {
            headingKey: "headings.customer",
            cellFunc: (s: StockAllocationRow) => {
                return s.customerName
            },
            isSearchable: true,
        },
        {
            headingKey: "headings.site",
            cellFunc: (s: StockAllocationRow) => {
                return s.siteName
            },
        },
        {
            headingKey: "headings.quantity",
            cellFunc: (s: StockAllocationRow) => {
                return s.quantity
            },
        },
        {
            headingKey: "headings.date",
            cellFunc: (s: StockAllocationRow) => {
                return s.date ?? null
            },
        },
        {
            headingKey: "headings.actions",
            cellFunc: (s: StockAllocationRow) => (
                <span style={{ display: "flex", gap: "1.5em" }}>
                    <Icon name='icon-edit-16' onClick={(event: React.MouseEvent<Element, MouseEvent>) => rightButtonsFunctions.edit(event, s)} />
                    <Icon name='icon-trash-16' onClick={(event: React.MouseEvent<Element, MouseEvent>) => rightButtonsFunctions.delete(event, s)} />
                    <Icon name='icon-shop-16' onClick={(event: React.MouseEvent<Element, MouseEvent>) => rightButtonsFunctions.dispatch(event, s)} />
                </span>
            ),
        },
    ]
    return (
        <>
            <PageTitle onPathChange={navigate} title={formatMessage("stockAllocation.title")} extraItems={rightButtons} />

            <div style={itemViewDetailsContainerStyles}>
                <Select
                    id='SockAllocationSearchInput'
                    icon={<Icon className='icon-search-16' />}
                    label={formatMessage("headings.partNumber")}
                    onChange={value => onSearchChange(convertValueToOption(value, listOfPartNumbers))}
                    options={listOfPartNumbers}
                    value={searchInput.text}
                    style={{ marginBottom: "0.5em" }}
                />
                <h5>{`Sensors Able to Commit: ${sensorsAbleToCommitNumber}`}</h5>
                <div style={tableContainerStyles}>
                    <ListView
                        noneYetKey={"stockAllocation.nonYet"}
                        template={tableTemplate}
                        data={dataTable}
                        onClickRow={(s: StockAllocationRow) => onClickRow(s)}
                    />
                </div>
            </div>
            {openAddAllocation && (
                <StockAllocateModal
                    isOpen={openAddAllocation}
                    onClose={rightButtonsFunctions.closeModal}
                    existAbleToCommitSensorsNumber={sensorsAbleToCommitNumber}
                    stockAllocateRow={stockAllocationToEdit}
                    partNumber={searchInput.text}
                />
            )}
            {openDeleteAllocation && (
                <StockAllocationDeleteModal
                    isOpen={openDeleteAllocation}
                    setIsOpen={setOpenDeleteAllocation}
                    macAddresses={stockAllocationToDelete ?? []}
                    partNumber={searchInput.text}
                />
            )}
        </>
    )
})

export default StockAllocationListView
